/*
 * The Global Calendar screen's state.
 *
 * The import result is kept until the next import rather than cleared on reload: a summary saying
 * "34 of 120 imported, 80 duplicates" is the only record of what the file did, and clearing it the
 * moment the list refreshes means nobody ever reads it.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, superadminApi } from '../../../api';
import type { ConcertCalendarDto, ConcertCalendarImportResultDto, ConcertImportDraftDto } from '../../../api';
import type { GlobalCalendarState } from './GlobalCalendarScreen';

export function useGlobalCalendar(token: string): GlobalCalendarState {
  const [entries, setEntries] = useState<ConcertCalendarDto[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState<ConcertCalendarImportResultDto | null>(null);
  const [importDraft, setImportDraft] = useState<ConcertImportDraftDto | null>(null);

  const load = useCallback(async () => {
    try {
      setEntries(await superadminApi.listConcertCalendar(token));
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดปฏิทินคอนเสิร์ตไม่สำเร็จ');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
      return false;
    } finally {
      setBusy(false);
    }
  }, [load]);

  return {
    entries: entries ?? [],
    loading: entries === null && !loadError,
    loadError, error, busy, importResult,
    importFile: async (file) => {
      await run(async () => {
        setImportResult(await superadminApi.importConcertCalendarFile(token, file));
      }, 'นำเข้าไฟล์ไม่สำเร็จ');
    },
    importDraft,
    /*
     * Fetches a draft and nothing else — the endpoint never writes, and neither does this.
     *
     * The draft is cleared before the request so that a second fetch cannot leave the previous
     * page's fields on screen while this one loads: the review form is one component reused per
     * URL, and a stale draft under a new link is a page's data saved under another page's name.
     */
    importFromUrl: async (url) => {
      setImportDraft(null);
      let draft: ConcertImportDraftDto | null = null;
      const ok = await run(async () => {
        draft = (await superadminApi.importConcertFromUrl(token, url)).draft;
      }, 'ดึงข้อมูลจากลิงก์ไม่สำเร็จ');
      if (ok && draft) setImportDraft(draft);
      return ok && draft !== null;
    },
    clearDraft: () => setImportDraft(null),
    /* Reports whether it saved, so the form can stay open on a refusal with the values intact. */
    create: (entry) => run(
      () => superadminApi.createConcertCalendarEntry(token, entry),
      'เพิ่มงานเข้าปฏิทินไม่สำเร็จ',
    ),
    remove: async (id) => {
      await run(() => superadminApi.deleteConcertCalendarEntry(token, id), 'ลบรายการไม่สำเร็จ');
    },
    reload: load,
  };
}
